// File: src/components/MatchFlow/MatchFlowContext.jsx
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useToaster } from '../../context/ToasterContext';
import useGeoSocket from '../../utils/useGeoSocket';

export const MatchFlowContext = createContext(null);

export function MatchFlowProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [match, setMatch] = useState(null);
  const [isFinding, setIsFinding] = useState(false);
  const [occupancy, setOccupancy] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);

  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');

  const { addToast } = useToaster();

  // 🔐 pull a token once so we can run the geo heartbeat across the whole flow
  const accessToken =
    JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
    localStorage.getItem('accessToken') ||
    '';

  // ✅ FAST geo heartbeat (watchPosition + 15s fallback) for ALL Speedmatch pages
  // This keeps occupancy/radius accurate even when the user navigates within the flow.
  useGeoSocket({ userId: currentUserId, token: accessToken });

  // Refs to stabilize state used inside listeners
  const matchRef = useRef(match);
  useEffect(() => { matchRef.current = match; }, [match]);

  const closingRef = useRef(false);

  const isMyMatch = useCallback((m) => {
    if (!m || !m.participants) return false;
    return m.participants.some(
      (p) => (p._id ? p._id.toString() : p) === currentUserId
    );
  }, [currentUserId]);

  // Persist the eventId (server truth) so pages can recover it across refreshes
  function stashEventId(maybeMatch) {
    try {
      const eid =
        maybeMatch?.event?._id ||
        maybeMatch?.eventId ||
        (Array.isArray(maybeMatch?.participants) ? null : null);
      if (eid) localStorage.setItem('mobileEventId', String(eid));
    } catch {}
  }

  /* ─────────────────────────────────────────────────────────
     Socket connection (create once per token) — StrictMode safe
  ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    const accessTokenLocal =
      JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
      localStorage.getItem('accessToken') || '';

    // No auth? Skip socket init (do NOT redirect).
    if (!accessTokenLocal) return;

    // Avoid double init under React 18 StrictMode
    const already = window.__mf_socket_init_done__;
    if (already) return;
    window.__mf_socket_init_done__ = true;

    const s = io(process.env.REACT_APP_API_URL, {
      auth: { token: accessTokenLocal },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      timeout: 8000,
    });

    setSocket(s);

    const onConnect = () => {
      console.log('[MatchFlowProvider] socket connected', s.id);
      // Refresh auth (token may have rotated)
      const fresh =
        JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
        localStorage.getItem('accessToken') || accessTokenLocal;
      try { s.auth = { token: fresh }; } catch {}
      if (currentUserId) s.emit('joinUserRoom', String(currentUserId));
    };

    const onConnectError = (e) =>
      console.warn('[MatchFlowProvider] socket connect_error', e?.message || e);

    s.on('connect', onConnect);
    s.on('connect_error', onConnectError);

    return () => {
      try {
        s.off('connect', onConnect);
        s.off('connect_error', onConnectError);
        s.disconnect();
      } catch {}
      setSocket(null);
      // allow re-init after unmount (e.g., logout → login)
      window.__mf_socket_init_done__ = false;
    };
  // Recreate socket only when user context changes
  }, [currentUserId]);

  /* ─────────────────────────────────────────────────────────
     Socket listeners (attach once per socket instance)
     + decline-exit guard to prevent local resurrection
  ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!socket) return;

    const declinedExitFor = (id) => {
      try { return sessionStorage.getItem(`sm:declineExit:${String(id)}`) === '1'; }
      catch { return false; }
    };

    const onOccupancyUpdated = (data) => {
      if (!data) return;
      const count = typeof data.occupancyCount === 'number'
        ? data.occupancyCount
        : (Array.isArray(data)
            ? data.reduce((acc, d) => (d?.occupancyCount ?? 0), 0)
            : 0);
      setOccupancy(count);
    };

    const onMatchUpdated = (updatedMatch) => {
      if (!updatedMatch) return;
      // Do not resurrect a match we locally declined this round
      if (declinedExitFor(updatedMatch._id)) return;
      if (!isMyMatch(updatedMatch)) return;

      // Calculate UI step
      if (updatedMatch.status === 'completed') {
        updatedMatch.step = 4;
      } else if (updatedMatch.codeConfirmed) {
        updatedMatch.step = 3;
      } else if (updatedMatch.status === 'in-session') {
        updatedMatch.step = 2;
      } else if (updatedMatch.status === 'accepted') {
        updatedMatch.step = 2;
      } else {
        updatedMatch.step = 1;
      }

      // Seed verify → inmatch success-hold (3.2s) on server confirmation
      try {
        if (updatedMatch?.codeConfirmed) {
          const eid =
            updatedMatch?.event?._id ||
            updatedMatch?.eventId ||
            localStorage.getItem('mobileEventId');
          if (eid) {
            const until = Date.now() + 3200; // 3.2s buffer
            sessionStorage.setItem(`speedmatch_verify_success_hold:${eid}`, String(until));
          }
        }
      } catch {}

      const current = matchRef.current;
      if (!current || updatedMatch._id === current._id) {
        setMatch(updatedMatch);
        stashEventId(updatedMatch); // 👈 persist eventId on every authoritative update
      }
    };

    const onMatchCreated = (newMatch) => {
      if (!newMatch) return;
      if (declinedExitFor(newMatch._id)) return; // skip reviving just-declined match
      closingRef.current = false;
      if (!isMyMatch(newMatch)) return;
      newMatch.step = 1;
      setMatch(newMatch);
      setIsFinding(false);
      stashEventId(newMatch); // 👈 persist as soon as we get a match
    };

    const onMatchFound = (newMatch) => {
      if (!newMatch) return;
      if (declinedExitFor(newMatch._id)) return; // skip reviving just-declined match
      closingRef.current = false;
      if (!isMyMatch(newMatch)) return;
      newMatch.step = 1;
      setMatch(newMatch);
      setIsFinding(false);
      stashEventId(newMatch); // 👈 persist here as well
    };

    const onSessionEnded = () => {
      const current = matchRef.current;
      if (!current || !current._id) return;
      const completedMatch = {
        ...current,
        status: 'completed',
        endTime: new Date().toISOString(),
      };
      setMatch(completedMatch);
      setIsFinding(false);
      removeSuccessBorder();
      stashEventId(completedMatch); // keep the last eventId around
    };

    const onMatchEnded = ({ matchId }) => {
      const current = matchRef.current;
      if (!current || current._id !== matchId) return;
      closingRef.current = false;
      const completedMatch = {
        ...current,
        status: 'completed',
        endTime: new Date().toISOString(),
      };
      setMatch(completedMatch);
      setIsFinding(false);
      removeSuccessBorder();
      stashEventId(completedMatch);
    };

    const onMatchExpired = ({ matchId, reason }) => {
      console.log('[Socket] => match_expired =>', matchId, reason);
      if (!closingRef.current) {
        closingRef.current = true;
        const current = matchRef.current;
        if (
          current &&
          current._id === matchId &&
          (current.status === 'completed' || current.status === 'declined')
        ) {
          console.log('[MatchFlowContext] => match_expired ignored, match already ended.');
          return;
        }
        // preserve eventId for enforcement even if match is cleared
        stashEventId(current);
        setMatch(null);
        setIsFinding(false);
        localStorage.setItem('expiredReason', reason);
        navigate('/matchflow?expired=1');
      }
    };

    const onWaiting = () => {
      closingRef.current = false;
      try {
        if (sessionStorage.getItem('__mf_manual_find_only__') !== '1') {
          console.warn('[MatchFlowContext] ignoring waiting (no manual find flag)');
          return;
        }
      } catch {}
      setIsFinding(true);
    };

    const onSearchCancelled = () => {
      closingRef.current = false;
      setIsFinding(false);
    };

    const onErrorMsg = (data) => {
      setIsFinding(false);
      setErrorMsg(data);
      console.warn('[MatchFlowProvider] => errorMsg =>', data);
    };

    // NEW: route_state pulse → rebroadcast as DOM event for pages/enforcer
    const onRouteState = (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('sm:route-pulse', { detail: payload }));
      } catch (err) {
        console.warn('[MatchFlowContext] route_state dispatch failed:', err);
      }
    };

    socket.on('occupancyUpdated', onOccupancyUpdated);
    socket.on('matchUpdated', onMatchUpdated);
    socket.on('matchCreated', onMatchCreated);
    socket.on('matchFound', onMatchFound);
    socket.on('sessionEnded', onSessionEnded);
    socket.on('match_ended', onMatchEnded);
    socket.on('match_expired', onMatchExpired);
    socket.on('waiting', onWaiting);
    socket.on('searchCancelled', onSearchCancelled);
    socket.on('errorMsg', onErrorMsg);
    socket.on('route_state', onRouteState); // 👈 add the pulse listener

    return () => {
      socket.off('occupancyUpdated', onOccupancyUpdated);
      socket.off('matchUpdated', onMatchUpdated);
      socket.off('matchCreated', onMatchCreated);
      socket.off('matchFound', onMatchFound);
      socket.off('sessionEnded', onSessionEnded);
      socket.off('match_ended', onMatchEnded);
      socket.off('match_expired', onMatchExpired);
      socket.off('waiting', onWaiting);
      socket.off('searchCancelled', onSearchCancelled);
      socket.off('errorMsg', onErrorMsg);
      socket.off('route_state', onRouteState); // 👈 cleanup
    };
  }, [socket, isMyMatch, navigate, setMatch, setIsFinding, setOccupancy]);

  /* ─────────────────────────────────────────────────────────
     UI helpers
  ───────────────────────────────────────────────────────── */
  function applySuccessBorderToInputs() {
    const inputEls = document.querySelectorAll('.code-input');
    inputEls.forEach((el) => {
      el.classList.remove('code-shake');
      el.classList.add('code-success');
    });
  }
  function removeSuccessBorder() {
    const inputEls = document.querySelectorAll('.code-input');
    inputEls.forEach((el) => {
      el.classList.remove('code-success');
    });
  }

  /* ─────────────────────────────────────────────────────────
     Contextual toasts — once per match round (sessionStorage)
     - Invite (pending)
     - Navigate (accepted | navigating)
     - In-match (in-session)
  ───────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!match?._id) return;

    const id = String(match._id);
    const stageKey = (stage) => `toast:${stage}:${id}`;

    const status = String(match.status || '').toLowerCase();

    // Invite / FoundMatch (pending)
    if (status === 'pending' && sessionStorage.getItem(stageKey('invite')) !== '1') {
      addToast('📝 Decide if you want to match.', 'info');
      try { sessionStorage.setItem(stageKey('invite'), '1'); } catch {}
    }

    // NavigateSpot (accepted | navigating)
    if ((status === 'accepted' || status === 'navigating') && sessionStorage.getItem(stageKey('navigate')) !== '1') {
      addToast('📍 Say hello and confirm where to meet.', 'info');
      try { sessionStorage.setItem(stageKey('navigate'), '1'); } catch {}
    }

    // In-Match (in-session)
    if (status === 'in-session' && sessionStorage.getItem(stageKey('inmatch')) !== '1') {
      addToast('💬 Enjoy your chat & respect the timer!', 'info');
      try { sessionStorage.setItem(stageKey('inmatch'), '1'); } catch {}
    }
  }, [match, addToast]);

  const value = {
    socket,
    match,
    setMatch,
    isFinding,
    setIsFinding,
    occupancy,
    setOccupancy,
    errorMsg,
    setErrorMsg,
  };

  return (
    <>
      <style>
        {`
          .code-success {
            border: 2px solid #2563EB !important;
          }
        `}
      </style>
      <MatchFlowContext.Provider value={value}>
        {children}
        {match?._id && (
          <div id="matchInfo" data-matchid={match._id} style={{ display: 'none' }} />
        )}
      </MatchFlowContext.Provider>
    </>
  );
}
